import { createTheme, ThemeProvider } from "@mui/material";
import { CommunicationChannel } from "../api";
import { PmsType, TenantContext, TenantContextValue, tenantDefaultContextValue } from "../tenant";
import { JSXElementConstructor, PropsWithChildren } from "react";
import { renderHook, RenderHookOptions, RenderHookResult } from "@testing-library/react";

/**
 * Manual mocking fetch
 * @param data
 * @param {boolean} ok
 * @param blob
 */
export const mockAPIResponse = (data: any, ok = true, blob?: Blob) => {
  const mockFetchPromise = Promise.resolve({
    ok,
    json: () => data,
    blob: () => blob,
    text: () => data
  });
  const globalRef: any = global;
  globalRef.fetch = jest.fn().mockImplementation(() => mockFetchPromise);
};

/**
 * Method is being used for mocking specific API
 * @param data object should contain url as key and value as response to API call
 * @param ok
 */
export const mockSpecificAPIResponse = (data: { [url: string]: any }, ok = true) => {
  const globalRef: any = global;
  globalRef.fetch = jest.fn().mockImplementation((url) => {
    if (Object.keys(data).includes(url)) {
      return Promise.resolve({
        ok,
        json: () => data[url],
        text: () => data[url]
      });
    }
    return Promise.reject(new Error(`${url} not mocked`));
  });
};

/**
 * Manual mocking fetch with error
 * @param error
 */
export const mockAPIError = (error: any) => {
  const mockFetchPromise = Promise.reject(error);
  const globalRef: any = global;
  globalRef.fetch = jest.fn().mockImplementation(() => mockFetchPromise);
};

export const resetMockAPI = () => {
  const globalRef: any = global;
  globalRef.fetch.mockClear();
};

export function renderHookWithTenantContext<Props, Result>(
  callback: (props: Props) => Result,
  tenantContextValue: Partial<TenantContextValue> = {},
  options: RenderHookOptions<Props> = {}
): RenderHookResult<Result, Props> {
  const { ...renderOptions } = options;
  const Wrapper: JSXElementConstructor<PropsWithChildren<any>> = ({ children }) => (
    <TenantContext.Provider value={{ ...tenantDefaultContextValue, ...tenantContextValue }}>
      <MockTheme>{children}</MockTheme>
    </TenantContext.Provider>
  );
  return renderHook(callback, { wrapper: Wrapper, ...renderOptions });
}

export const mockEventSourcePolyfill = () => {
  const globalRef: any = global;
  globalRef.EventSourcePolyfill = jest.fn().mockImplementation(() => ({
    addEventListener: jest.fn(),
    close: jest.fn()
  }));
};

export const resetMockEventSourcePolyfill = () => {
  const globalRef: any = global;
  globalRef.EventSourcePolyfill.mockClear();
};

export const mockHostname = (tenantId: string) => {
  delete (window as any).location;

  Object.defineProperty(window, "location", {
    configurable: true,
    enumerable: true,
    value: {
      hostname: `http://local.${tenantId}.com`,
      replace: jest.fn()
    },
    writable: true
  });
};

export const MockTheme = ({ children }: any) => {
  const theme = createTheme({});
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

/* START Mock data for each app */
const mockedTenantId = "staykooook-test";

export const gjTenant = {
  tenantId: mockedTenantId,
  keycloak: {
    realm: "realm",
    "auth-server-url": "http://localhost",
    "ssl-required": "external",
    resource: "resource",
    "public-client": true,
    "verify-token-audience": true,
    "use-resource-role-mappings": true,
    "confidential-port": 0
  },
  theme: "likemagic-theme",
  adyen: {
    clientKey: "clientKey",
    environment: "environment"
  },
  prismic: {
    apiEndpoint: "http://localhost",
    accessToken: "accessToken"
  },
  features: {
    authEnabled: true,
    enableExtendStay: true,
    bookingAppIntegrated: true
  },
  allowedBackofficeLanguages: ["en", "de"],
  allowedLanguages: ["en", "de", "it", "fr"],
  allowedLocales: ["en-us", "de-ch"],
  apiKey: "apiKey",
  toneOfVoice: "formal",
  urls: {
    bookingAppUrl: "http://localhost"
  },
  pms: PmsType.APALEO,
  gtmId: "",
  search: undefined,
  enabledCommunicationChannels: [CommunicationChannel.SMS, CommunicationChannel.EMAIL],
  additionalTravelBuddyFields: [],
  earliestCheckinOffset: 8,
  properties: {
    BRNEE: {
      baseUrls: ["http://localhost"],
      saltoKS: {
        siteId: "test-site-id",
        tagReaderIds: ["test-tag-reader-id-1"]
      },
      frontend: {
        theme: "staykooook-theme",
        toneOfVoice: "informal",
        doorConfig: {
          doorProvider: "SALTO",
          batteryLevelSupported: true,
          mainDoorPrefixes: ["Main", "Front", "Haupteingang"],
          timeAvailability: {
            publicDoorsAvailableFrom: { hours: 8, minutes: 0 },
            publicDoorsAvailableTo: { hours: 8, minutes: 0 }
          }
        }
      }
    }
  },
  kiosk: {
    paymentByTerminalOnly: false,
    hideAssignKey: false,
    enforceScanUi: false,
    tagReaderId: "abcdefg",
    propertyId: null,
    paymentByTerminalId: "12345678",
    availableOptions: ["FIND_RESERVATION", "CREATE_KEY"]
  },
  themeOverride: undefined
};
export const backofficeTenant = {
  tenantId: mockedTenantId,
  pms: PmsType.APALEO,
  keycloak: {
    realm: "realm",
    "auth-server-url": "http://localhost",
    "ssl-required": "external",
    resource: "resource",
    "public-client": true,
    "verify-token-audience": true,
    "use-resource-role-mappings": true,
    "confidential-port": 0
  },
  theme: "staykooook-theme",
  apiKey: "apiKey",
  routesWithAuthToken: ["/api/guest-journey-service"]
};

export const monitoringTenant = {
  tenantId: mockedTenantId,
  pms: PmsType.APALEO,
  keycloak: {
    realm: "realm",
    "auth-server-url": "http://localhost",
    "ssl-required": "external",
    resource: "resource",
    "public-client": true,
    "verify-token-audience": true,
    "use-resource-role-mappings": true,
    "confidential-port": 0
  },
  theme: "staykooook-theme",
  apiKey: "apiKey",
  urls: {
    apaleoUrl: "https://app.apaleo.com",
    zeebeUrl: "https://bru-2.operate.camunda.io/a2f274c1-76fd-42df-bee2-5667d9978552"
  },
  routesWithAuthToken: [
    "/api/reservation-service/",
    "/api/freshdesk-service/",
    "/api/integration-service/"
  ]
};
/* END Mock data for each app */
